import { useState } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';

/**
 * Admin bulk-import of sites from CSV, presented as a modal on Manage sites.
 *
 * Public interface: `isOpen` + `onClose`. Hides all upload state (file,
 * progress, per-row result/errors) and refreshes the sites list on success.
 */
export default function SiteImportModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleClose = () => {
    if (importLoading) return;
    setImportFile(null);
    setImportResult(null);
    onClose();
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { data } = await api.post('/import/sites', formData);
      setImportResult(data);
      queryClient.invalidateQueries('sites');
      if (data.imported > 0 || data.updated > 0) {
        const parts = [];
        if (data.imported > 0) parts.push(`${data.imported} new`);
        if (data.updated > 0) parts.push(`${data.updated} updated`);
        toast.success(`Sites: ${parts.join(', ')}`);
      }
      if (data.errors?.length > 0) toast.error(`${data.errors.length} row(s) had errors`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
      setImportResult({
        imported: 0,
        errors: [{ message: err.response?.data?.error || 'Import failed' }],
      });
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import sites (CSV)" className="settings-import-card">
      <p className="import-hint" style={{ marginTop: 0 }}>
        CSV must include a <strong>Name</strong> column (site name). Optional: Site Code, Address,
        City, State, Postcode, Contact Name, Contact Phone, Contact Email, Notes,{' '}
        <strong>Testing Due Date</strong> (DD/MM/YYYY or YYYY-MM-DD).
      </p>

      {!importResult ? (
        <form onSubmit={handleImportSubmit}>
          <div className="form-group">
            <label htmlFor="site-import-csv">CSV file</label>
            <input
              id="site-import-csv"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              disabled={importLoading}
            />
          </div>
          {importLoading && (
            <div className="import-progress">
              <div className="import-progress-bar" />
              <p>Processing…</p>
            </div>
          )}
          <div className="form-actions">
            <Button type="button" onClick={handleClose} disabled={importLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={importLoading || !importFile}>
              {importLoading ? 'Importing…' : 'Import'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="import-result">
          <p className="import-success">
            {importResult.imported > 0 && (
              <>
                Imported <strong>{importResult.imported}</strong> new site
                {importResult.imported !== 1 ? 's' : ''}.{' '}
              </>
            )}
            {importResult.updated > 0 && (
              <>
                Updated <strong>{importResult.updated}</strong> existing site
                {importResult.updated !== 1 ? 's' : ''} (by Site Code).{' '}
              </>
            )}
            {importResult.imported === 0 && importResult.updated === 0 && 'No sites added or updated.'}
            {importResult.totalRows != null && (importResult.imported > 0 || importResult.updated > 0) && (
              <> ({importResult.totalRows} row{importResult.totalRows !== 1 ? 's' : ''} in file)</>
            )}
          </p>
          {importResult.errors?.length > 0 && (
            <div className="import-errors">
              <strong>Errors ({importResult.errors.length}):</strong>
              <ul>
                {importResult.errors.slice(0, 20).map((err, idx) => (
                  <li key={idx}>
                    {err.row != null ? `Row ${err.row}: ` : ''}{err.message}
                  </li>
                ))}
                {importResult.errors.length > 20 && (
                  <li>… and {importResult.errors.length - 20} more</li>
                )}
              </ul>
            </div>
          )}
          <div className="form-actions">
            <Button type="button" onClick={() => { setImportResult(null); setImportFile(null); }}>
              Import another file
            </Button>
            <Button type="button" variant="primary" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
